import { useState, useEffect } from 'react';
import { Menu, User, LogOut, Key, Camera, Shield } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { colors } from '../styles/colors';
import logo from '../assets/Nimbus_logo_only.png';
import ChangePasswordModal from './ChangePasswordModal';
import UpdatePhotoModal from './UpdatePhotoModal';
import PasskeyModal from './PasskeyModal';
import logoName from '../assets/Nimbus_logo_name.png';

function Topbar({ onToggleSidebar, isCollapsed }) {
  const { user, logout, updateUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [photoError, setPhotoError] = useState(false);

  // Fermer le menu déroulant quand on change de page
  useEffect(() => {
    setShowDropdown(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'consultant':
        return 'Consultant';
      case 'visiteur':
        return 'Visiteur';
      default:
        return role;
    }
  };

  const handlePhotoUpdate = async (photoData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/.netlify/functions/updateUserPhoto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          photoData: photoData
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Mettre à jour les informations utilisateur dans le contexte
        updateUser(data.user);
        showToast(data.message, 'success');
      } else {
        showToast(data.error || 'Erreur lors de la mise à jour de la photo', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showToast('Erreur lors de la mise à jour de la photo', 'error');
    }
  };

  return (
    <header
      style={{
        height: '64px',
        backgroundColor: colors.text.white,
        borderBottom: `1px solid ${colors.secondary.mediumGray}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 1.5rem',
        gap: '1.5rem',
        position: 'fixed',
        top: 0,
        left: isCollapsed ? '112px' : '288px',
        right: 0,
        zIndex: 40,
        transition: 'left 300ms ease-in-out',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* Bouton hamburger */}
      <button
        onClick={onToggleSidebar}
        style={{
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: '0.5rem',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          transition: 'background-color 200ms',
          color: colors.primary.gray
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightGray}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Menu"
      >
        <Menu size={24} />
      </button>

      {/* Logo et nom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
        <img
          src={logo}
          alt="Ephyco"
          style={{
            height: '45px',
            width: 'auto'
          }}
        />
        <img
          src={logoName}
          alt="Ephyco"
          style={{
            height: '30px',
            width: 'auto'
          }}
        />
      </div>

      {/* Profil utilisateur */}
      {user && (
        <div style={{ position: 'relative' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div style={{ textAlign: 'right' }}>
              <p style={{
                margin: 0,
                fontSize: '0.875rem',
                fontWeight: '500',
                color: colors.primary.gray,
                fontFamily: '"Noto Sans", sans-serif'
              }}>
                {user.prenom} {user.nom}
              </p>
              <p style={{
                margin: 0,
                fontSize: '0.75rem',
                color: colors.secondary.mediumGray,
                fontFamily: '"Noto Sans", sans-serif'
              }}>
                {getRoleName(user.role)}
              </p>
            </div>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: user.photo_url ? 'transparent' : colors.secondary.lightTeal,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.primary.teal,
                overflow: 'hidden',
                border: user.photo_url ? 'none' : 'none'
              }}
            >
              {user.photo_url && !photoError ? (
                <img
                  src={user.photo_url}
                  alt={`${user.prenom} ${user.nom}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={() => setPhotoError(true)}
                />
              ) : (
                <User size={20} />
              )}
            </div>
          </div>

          {/* Dropdown menu */}
          {showDropdown && (
            <>
              {/* Overlay pour fermer le dropdown */}
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 49
                }}
                onClick={() => setShowDropdown(false)}
              />

              {/* Menu */}
              <div
                style={{
                  position: 'absolute',
                  top: '50px',
                  right: 0,
                  backgroundColor: colors.text.white,
                  border: `1px solid ${colors.secondary.mediumGray}`,
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  minWidth: '220px',
                  zIndex: 50,
                  fontFamily: '"Noto Sans", sans-serif'
                }}
              >
                <button
                  onClick={() => {
                    setShowPhotoModal(true);
                    setShowDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    border: 'none',
                    borderBottom: `1px solid ${colors.secondary.lightGray}`,
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: colors.primary.gray,
                    fontFamily: '"Noto Sans", sans-serif',
                    transition: 'background-color 200ms'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.secondary.lightGray;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Camera size={18} />
                  Modifier ma photo
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(true);
                    setShowDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    border: 'none',
                    borderBottom: `1px solid ${colors.secondary.lightGray}`,
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: colors.primary.gray,
                    fontFamily: '"Noto Sans", sans-serif',
                    textAlign: 'left',
                    transition: 'background-color 200ms'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.secondary.lightGray;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Key size={18} />
                  Changer le mot de passe
                </button>
                <button
                  onClick={() => {
                    setShowPasskeyModal(true);
                    setShowDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    border: 'none',
                    borderBottom: `1px solid ${colors.secondary.lightGray}`,
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: colors.primary.gray,
                    fontFamily: '"Noto Sans", sans-serif',
                    textAlign: 'left',
                    transition: 'background-color 200ms'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.secondary.lightGray;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Shield size={18} />
                  Mes clés de sécurité
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#dc2626',
                    fontFamily: '"Noto Sans", sans-serif',
                    transition: 'background-color 200ms'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <LogOut size={18} />
                  Déconnexion
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal de changement de mot de passe */}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      {/* Modal de modification de photo */}
      {showPhotoModal && (
        <UpdatePhotoModal
          isOpen={showPhotoModal}
          onClose={() => setShowPhotoModal(false)}
          currentPhoto={user?.photo_url}
          onPhotoUpdate={handlePhotoUpdate}
          userName={`${user?.prenom} ${user?.nom}`}
        />
      )}

      {/* Modal de gestion des passkeys */}
      {showPasskeyModal && (
        <PasskeyModal onClose={() => setShowPasskeyModal(false)} />
      )}
    </header>
  );
}

export default Topbar;